import React, { useEffect, useState } from 'react'

import { StyleProvider } from '@ant-design/cssinjs'
import { IMember } from '@cloudike/web_photos/dist/types/intarfaces/IMember'
import { Button, message, Select, Tooltip } from 'antd'
import { selectCollaborators } from 'features/common/model/selectors/sharingSelectors'
import { addCollaboratorsThunk } from 'features/common/model/slice/sharingSlice'
import { familyMembersSelector } from 'features/team/model/selectors/teamSelectors'
import { userSelector } from 'features/user/model/selectors/userSelectors'
import { useAppDispatch, useAppSelector } from 'shared/config/store'
import { createGlobalStyle, styled } from "styled-components"

interface ExtendedMember extends IMember {
  isSelectable?: boolean;
  phone_or_email?: string;
  email?: string;
}

function validateEmail(
  email: string,
  allAddedEmails: string[],
  currentUserEmail: string,
  familyMembers: ExtendedMember[]
): string | null {
  const trimmedEmail = email.trim()

  if (!trimmedEmail) return "Email cannot be empty"
  
  if (/[а-яё]/i.test(trimmedEmail)) {
    return "Email can only contain English letters"
  }
  
  const normalizedEmail = trimmedEmail.toLowerCase()
  const normalizedCurrentUserEmail = currentUserEmail.toLowerCase()
  const normalizedAllAddedEmails = allAddedEmails.map(e => e.toLowerCase())
  
  if (normalizedAllAddedEmails.includes(normalizedEmail)) return "This email has already been added."
  if (normalizedEmail === normalizedCurrentUserEmail) return "You cannot invite yourself."
  
  const existingUser = familyMembers.find(member => {
    const memberEmail = (member.phone_or_email || member.email || '').toLowerCase()
    return memberEmail === normalizedEmail
  })
  
  if (existingUser) {
    return "This user is already a member of your team."
  }
  
  const atParts = trimmedEmail.split('@')
  if (atParts.length !== 2) return "Invalid email"

  const [localPart, domainPart] = atParts
  if (!localPart || !domainPart) return "Invalid email"
  if (/\s/.test(domainPart)) return "Invalid email"
  if (localPart.startsWith('.') || localPart.endsWith('.')) return "Invalid email"
  if (!domainPart.includes('.')) return "Invalid email"
  
  const domainParts = domainPart.split('.')
  if (domainParts.length < 2) return "Invalid email domain"
  
  const domainTld = domainParts[domainParts.length - 1]
  if (!domainTld || domainTld.length < 2) return "Invalid email domain"
  
  if (localPart.length === 0) return "Invalid email"
  
  if (domainPart.startsWith('.') || domainPart.endsWith('.')) return "Invalid email domain"

  return null
}

const CustomTooltipStyles = createGlobalStyle`
  .ant-tooltip-content{
    background-color: rgba(0, 0, 0, 0.85);
    border-radius: 6px;
  }

 .ant-tooltip{
    background-color: rgba(0, 0, 0, 0.85);
    max-width: 356px;
    border-radius: 6px;
  }
`


const CollaboratorsInputInternal = ({ onValueChange }: { onValueChange: (value: string) => void }) => {
  const dispatch = useAppDispatch()
  const currentUser = useAppSelector(userSelector)
  const familyMembers = useAppSelector(familyMembersSelector) as ExtendedMember[]
  const collaborators = useAppSelector(selectCollaborators)
    
  const currentFamilyMember = familyMembers.find(member => member.id === String(currentUser?.id))
  const currentUserEmail = currentFamilyMember?.phone_or_email || currentFamilyMember?.email || currentUser?.email || ''
  
  const [tags, setTags] = useState<string[]>([])
  const [inputValue, setInputValue] = useState('')
  
  useEffect(() => {
    onValueChange(tags.join(','))
  }, [tags, onValueChange])
  
  const hasValidEmailInInput = () => {
    if (!inputValue.trim()) return false
    
    const collaboratorEmails = collaborators.map(c => c.phone_or_email)
    const allAddedEmails = [...collaboratorEmails, ...tags]
    const error = validateEmail(inputValue, allAddedEmails, currentUserEmail, familyMembers)
    
    return error === null
  }
  
  const handleAddTag = (email: string) => {
    const collaboratorEmails = collaborators.map(c => c.phone_or_email)
    const allAddedEmails = [...collaboratorEmails, ...tags]
    const error = validateEmail(email, allAddedEmails, currentUserEmail, familyMembers)

    if (error) {
      message.error(error)
    } else {
      setTags([...tags, email.trim().toLowerCase()])
    }
    setInputValue('')
  }

  const handleInvite = () => {
    const validEmails: string[] = []
    const collaboratorEmails = collaborators.map(c => c.phone_or_email)

    tags.forEach(tag => {
      const allOtherEmails = [...collaboratorEmails, ...validEmails]
      const error = validateEmail(tag, allOtherEmails, currentUserEmail, familyMembers)
      if (error) {
        message.error(`"${tag}": ${error}`)
      } else {
        validEmails.push(tag)
      }
    })

    if (validEmails.length > 0) {
      dispatch(addCollaboratorsThunk(validEmails))
    }
    setTags([])
    setInputValue('')
  }
  
  const handleKeyDown = (e: React.KeyboardEvent) => {
    if ((e.key === 'Enter' || e.key === ' ') && inputValue) {
      e.preventDefault()
      handleAddTag(inputValue)
    }
  }
  
  const handleBlur = () => {
    if(inputValue) {
      handleAddTag(inputValue)
    }
  }

  const handleTagsChange = (newTags: string[]) => {
    if (newTags.length < tags.length) {
      setTags(newTags)
    }
  }

  const isInviteButtonDisabled = tags.length === 0 && !hasValidEmailInInput()

  return (

    <CollaboratorsInputContainer>
      <Tooltip
        title="Enter email(s) to send the link, invited users need log in to open the shared content"
        placement="bottom"
        overlayClassName="input-tooltip"
      >
        <StyledSelect
          mode="tags"
          placeholder="Invite by email"
          value={tags}
          onChange={handleTagsChange}
          onSearch={setInputValue}
          onKeyDown={handleKeyDown}
          onBlur={handleBlur}
          searchValue={inputValue}
          notFoundContent={null}
          style={{ width: '100%', maxWidth: '355px' }}
          showArrow={false}
          open={false}
          dropdownRender={() => null}
        />
      </Tooltip>

      <Button 
        type="primary" 
        style={{ height: '40px' }} 
        onClick={handleInvite} 
        disabled={isInviteButtonDisabled}
      >
        Invite
      </Button>
    </CollaboratorsInputContainer>

  )
}

export const CollaboratorsInput = ({ onValueChange }: { onValueChange: (value: string) => void }) => (
  <StyleProvider hashPriority="high">
    <CustomTooltipStyles />

    <CollaboratorsInputInternal onValueChange={onValueChange} />
  </StyleProvider>
)

const CollaboratorsInputContainer = styled.div`
  display: flex;
  align-items: center;
  gap: 10px;
  margin-top: 20px;
`

const StyledSelect = styled(Select)`
  .ant-select-selector {
    padding-top: 4px !important;
    padding-bottom: 4px !important;
    align-items: center;  

    &:hover{
    cursor: pointer;
  }
  }

  
` 